import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await api.auth.getCurrentUser();

        if (response.success) {
          // Allow both ADMIN and INSTRUCTOR roles
          if (response.data.role === "ADMIN" || response.data.role === "INSTRUCTOR") {
            setUser(response.data);
          } else {
            // If not admin or instructor, logout and show message
            logout("Access denied. Only administrators and instructors can access this panel.");
          }
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await api.auth.login({ email, password });

      if (!response.success) {
        return {
          success: false,
          message: response.message || "Failed to login",
        };
      }

      // Allow both ADMIN and INSTRUCTOR roles
      if (response.data.role !== "ADMIN" && response.data.role !== "INSTRUCTOR") {
        // Logout immediately if not admin or instructor
        await api.auth.logout();
        return {
          success: false,
          message: "Access denied. Only administrators and instructors can access this panel.",
        };
      }

      // Set user state from response
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.message || "Failed to login",
      };
    }
  };

  // Logout function
  const logout = async (message) => {
    try {
      // Call the logout API if user is logged in
      if (user) {
        await api.auth.logout();
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local state
      setUser(null);

      // If message is provided, store it to display on sign-in page
      if (message) {
        sessionStorage.setItem("auth_message", message);
      }

      navigate("/sign-in");
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
